/**
 * Billing-area route bundle — invoicing, prepaid tracking, and account
 * management pages for accounting and staff users. Gated behind the
 * per-tenant EXCLUDE_BILLING build flag so the whole area (and its lazy
 * chunks) drops out of a tenant build that does not license billing.
 *
 * BillingLayout owns the AppNav chrome for every /billing/* route; its
 * children keep each page's own auth guard (accounting-user vs. staff),
 * since those differ per page and the layout itself stays unguarded.
 * BillingAccountLayout nests one level deeper for a single account's
 * subviews (invoices/pricing/prepaid/trial/catalog — there is no general
 * tab; its content lives permanently in the account rail), all behind a
 * single accounting-user guard. The index child redirects to invoices, the
 * default landing tab.
 *
 * Contains:
 * - billingRoutes — the /billing route tree, or null when excluded
 */
import React from 'react';
import { Navigate, Route } from 'react-router-dom';

import { determineIsAccountingUser, determineIsStaff } from '@sophocles/router/checkCredentials';
import { billingDashboardPath } from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';

// --- Build-time DCE block ---
// Gated on the bare DefinePlugin global so webpack can prune the lazy()
// import() calls (and the resulting chunks) at build time when the flag
// is true.  Never extract the global into a variable — that would defeat
// tree-shaking.

export let billingRoutes = null;
if (!__EXCLUDE_BILLING__) {
  const BillingLayout = React.lazy(() => import('@sophocles/views/billing/BillingLayout'));
  const PrepaidDashboardView = React.lazy(() => import('@sophocles/views/billing/PrepaidDashboardView'));
  const BillingAccountLayout = React.lazy(() => import('@sophocles/views/billing/BillingAccountLayout'));
  const BillingAccountsView = React.lazy(() => import('@sophocles/views/billing/BillingAccountsView'));
  const InvoiceDetailView = React.lazy(() => import('@sophocles/views/billing/InvoiceDetailView'));
  const TrialUnitsView = React.lazy(() => import('@sophocles/views/billing/TrialUnitsView'));
  const AccountInvoicesView = React.lazy(() => import('@sophocles/views/billing/account/AccountInvoicesView'));
  const AccountPricingView = React.lazy(() => import('@sophocles/views/billing/account/AccountPricingView'));
  const AccountPrepaidView = React.lazy(() => import('@sophocles/views/billing/account/AccountPrepaidView'));
  const AccountTrialUnitsView = React.lazy(() => import('@sophocles/views/billing/account/AccountTrialUnitsView'));
  const AccountCatalogView = React.lazy(() => import('@sophocles/views/billing/account/AccountCatalogView'));

  billingRoutes = (
    <Route path={billingDashboardPath.pattern} element={<BillingLayout />}>
      <Route index element={<Navigate to="accounts" replace />} />
      <Route
        path="invoices/:invoiceId"
        element={<RequireAuth component={InvoiceDetailView} onEnter={determineIsStaff} />}
      />
      <Route
        path="accounts"
        element={<RequireAuth component={BillingAccountsView} onEnter={determineIsAccountingUser} />}
      />
      <Route
        path="accounts/:accountId"
        element={<RequireAuth component={BillingAccountLayout} onEnter={determineIsAccountingUser} />}
      >
        <Route index element={<Navigate to="invoices" replace />} />
        <Route path="invoices" element={<AccountInvoicesView />} />
        <Route path="pricing" element={<AccountPricingView />} />
        <Route path="prepaid" element={<AccountPrepaidView />} />
        <Route path="trial" element={<AccountTrialUnitsView />} />
        <Route path="catalog" element={<AccountCatalogView />} />
      </Route>
      <Route
        path="prepaid-tracking"
        element={<RequireAuth component={PrepaidDashboardView} onEnter={determineIsStaff} />}
      />
      <Route path="trial-units" element={<RequireAuth component={TrialUnitsView} onEnter={determineIsStaff} />} />
    </Route>
  );
}
